import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { ChatState, ChatAction } from '../types';

// Initial state - start with empty messages
const initialState: ChatState = {
  messages: [],
  typingUsers: new Set<string>(),
  connectionStatus: 'disconnected', // Start disconnected until SSE connects
  currentUser: 'user',
  layoutMode: 'head', // Start with just the chat head
  isRecording: false,
  audioQueue: [],
  showIntro: false,
};

// Chat reducer function
export const chatReducer = (state: ChatState, action: ChatAction): ChatState => {
  switch (action.type) {
    case 'ADD_MESSAGE':
      return {
        ...state,
        messages: [...state.messages, action.payload],
        // Clear typing indicator for the sender when they send a message
        typingUsers: new Set(Array.from(state.typingUsers).filter(userId => userId !== action.payload.sender)),
      };

    case 'SET_TYPING':
      const newTypingUsers = new Set(state.typingUsers);
      if (action.payload.isTyping) {
        newTypingUsers.add(action.payload.userId);
      } else {
        newTypingUsers.delete(action.payload.userId);
      }
      return {
        ...state,
        typingUsers: newTypingUsers,
      };

    case 'SET_CONNECTION_STATUS':
      return {
        ...state,
        connectionStatus: action.payload.status,
      };

    case 'SET_LAYOUT_MODE':
      return {
        ...state,
        layoutMode: action.payload,
      };

    case 'START_RECORDING':
      return {
        ...state,
        isRecording: true,
      };

    case 'STOP_RECORDING':
      return {
        ...state,
        isRecording: false,
      };

    case 'QUEUE_AUDIO':
      return {
        ...state,
        audioQueue: [...state.audioQueue, action.payload],
      };

    case 'DEQUEUE_AUDIO':
      return {
        ...state,
        audioQueue: state.audioQueue.slice(1),
      };

    case 'DISMISS_INTRO':
      return {
        ...state,
        showIntro: false,
      };

    case 'SYNC_MESSAGES':
      // Merge new messages with existing ones, avoiding duplicates
      const existingMessageIds = new Set(state.messages.map(msg => msg.id));
      const newMessages = action.payload.filter(msg => !existingMessageIds.has(msg.id));
      return {
        ...state,
        messages: [...state.messages, ...newMessages].sort((a, b) => 
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        ),
      };

    case 'SET_LAST_SYNC_TIME':
      return {
        ...state,
        lastSyncTime: action.payload,
      };

    case 'SET_INITIAL_MESSAGE':
      // Only set initial message if there are no existing messages
      if (state.messages.length === 0) {
        return {
          ...state,
          messages: [action.payload],
        };
      }
      return state;

    case 'UPDATE_MESSAGE_CONTENT':
      return {
        ...state,
        messages: state.messages.map(msg => {
          if (msg.id === action.payload.messageId) {
            if (action.payload.isThinking) {
              // Update thinking content
              return {
                ...msg,
                thinking: (msg.thinking || '') + action.payload.content,
                isStreaming: true,
              };
            } else {
              // Update main content
              return {
                ...msg,
                content: msg.content + action.payload.content,
                isStreaming: true,
              };
            }
          }
          return msg;
        }),
      };

    case 'COMPLETE_MESSAGE_STREAM':
      return {
        ...state,
        messages: state.messages.map(msg => {
          if (msg.id === action.payload.messageId) {
            return {
              ...msg,
              isStreaming: false,
              streamingComplete: true,
            };
          }
          return msg;
        }),
      };

    case 'CLEAR_MESSAGES':
      return {
        ...state,
        messages: [],
        typingUsers: new Set<string>(),
        audioQueue: [],
        showIntro: false,
      };

    case 'NEW_CHAT_SESSION':
      return {
        ...state,
        messages: [],
        typingUsers: new Set<string>(),
        audioQueue: [],
        showIntro: true, // Show intro for new session
        connectionStatus: 'disconnected', // Reset connection
      };

    default:
      return state;
  }
};

// Context type
interface ChatContextType {
  state: ChatState;
  dispatch: React.Dispatch<ChatAction>;
}

// Create context
const ChatContext = createContext<ChatContextType | undefined>(undefined);

// Provider component
interface ChatProviderProps {
  children: ReactNode;
  initialUser?: string;
  initialMode?: 'head' | 'popup' | 'fullscreen';
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ 
  children, 
  initialUser = 'user',
  initialMode = 'head'
}) => {
  const [state, dispatch] = useReducer(chatReducer, {
    ...initialState,
    currentUser: initialUser,
    layoutMode: initialMode,
  });

  return (
    <ChatContext.Provider value={{ state, dispatch }}>
      {children}
    </ChatContext.Provider>
  );
};

// Custom hook to use chat context
export const useChatContext = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};